import {useMemo} from "react";
import type {
    ChecklistData,
    CollectionDataResponse,
} from "../../types";
import {filterTocByViewFilters} from "../utils";

/**
 * Computes the active label selection per view group, filters the TOC
 * by those labels, and determines the effective root node ID.
 *
 * Changes when: collection or dimension selection changes.
 */
export const useViewFiltering = (
    checklistData: ChecklistData,
    collectionData: CollectionDataResponse | undefined,
    defaultCollectionData: CollectionDataResponse | undefined,
) => {
    const labelData = useMemo(() => {
        const ret: Record<string, string> = {};
        const viewGroups = checklistData.viewGroups ?? [];
        if (!viewGroups.length) return ret;

        const collectionLabelIds = new Set(
            (collectionData?.labels ?? []).map((l) => l.labelId),
        );
        const defaultCollectionLabelIds = new Set(
            (defaultCollectionData?.labels ?? []).map((l) => l.labelId),
        );

        viewGroups.forEach((group) => {
            const label =
                group.labels.find((l) => collectionLabelIds.has(l.id)) ??
                group.labels.find((l) =>
                    defaultCollectionLabelIds.has(l.id),
                ) ??
                group.labels[0];
            if (label) ret[group.id] = label.id;
        });
        return ret;
    }, [collectionData, defaultCollectionData, checklistData]);

    const viewFilteredToc = useMemo(
        () =>
            filterTocByViewFilters(
                checklistData.toc,
                labelData,
                checklistData.objects ?? {},
            ),
        [checklistData, labelData],
    );

    const effectiveRootNodeId = viewFilteredToc[
        checklistData.rootNodeId ?? ""
    ]
        ? (checklistData.rootNodeId ?? "")
        : "";

    return {labelData, viewFilteredToc, effectiveRootNodeId};
};
